import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import {
  rawMaterialService,
  type RawMaterialListParams,
} from "@/services/raw-material";

import type { UpdateRawMaterialReq } from "@artisancode/api-types";

export function useRawMaterials(params?: RawMaterialListParams) {
  return useQuery({
    queryKey: queryKeys.rawMaterials.list(params as Record<string, unknown>),
    queryFn: () => rawMaterialService.list(params),
  });
}

export function useRawMaterial(id?: string) {
  return useQuery({
    queryKey: queryKeys.rawMaterials.detail(id || ""),
    queryFn: () =>
      id ? rawMaterialService.getById(id) : Promise.reject("No id"),
    enabled: Boolean(id),
  });
}

export function useRawMaterialUsage(id?: string) {
  return useQuery({
    queryKey: ["raw-materials", "usage", id || ""],
    queryFn: () =>
      id ? rawMaterialService.getUsage(id) : Promise.reject("No id"),
    enabled: Boolean(id),
  });
}

export function useCreateRawMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: rawMaterialService.create,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.rawMaterials.all }),
  });
}

export function useUpdateRawMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateRawMaterialReq) =>
      rawMaterialService.update(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.rawMaterials.all }),
  });
}

export function useDeleteRawMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rawMaterialService.delete(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.rawMaterials.all }),
  });
}
